import React from 'react';
import './ui.css';

export interface TabItem<K extends string = string> {
  key: K;
  label: React.ReactNode;
  /** Shown as a small count when > 0. */
  count?: number;
}

export interface TabsProps<K extends string = string> {
  tabs: TabItem<K>[];
  active: K;
  onChange: (key: K) => void;
  className?: string;
  'aria-label'?: string;
}

export function Tabs<K extends string = string>({ tabs, active, onChange, className, ...rest }: TabsProps<K>) {
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const i = tabs.findIndex(t => t.key === active);
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    if (next) onChange(next.key);
  };
  return (
    <div role="tablist" className={['ui-tabs', className].filter(Boolean).join(' ')} onKeyDown={onKeyDown} {...rest}>
      {tabs.map(tab => {
        const selected = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className="ui-tab"
            onClick={() => onChange(tab.key)}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && <span className="ui-tab__count">{tab.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
